import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "../../lib/utils"

const Breadcrumb = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<"nav"> & {
    separator?: React.ReactNode
  }
>(({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />)
Breadcrumb.displayName = "Breadcrumb"

const BreadcrumbList = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<"ol">
>(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
      className
    )}
    {...props}
  />
))
BreadcrumbList.displayName = "BreadcrumbList"

export type CollapsingBreadcrumbCrumb = {
  key: string
  node: React.ReactNode
  /** Classes on the BreadcrumbItem (e.g. `hidden md:block`). */
  itemClassName?: string
  /** Classes on the separator after this crumb (except when it is last). */
  separatorClassName?: string
  /** Truncate long labels inside the item (useful for file names). */
  truncate?: boolean
}

function twoLineMaxHeightPx(list: HTMLElement) {
  const styles = window.getComputedStyle(list)
  const lineHeight = Number.parseFloat(styles.lineHeight)
  const gap = Number.parseFloat(styles.rowGap || styles.gap || "0")
  const resolvedLine =
    Number.isFinite(lineHeight) && lineHeight > 0
      ? lineHeight
      : Number.parseFloat(styles.fontSize) * 1.25 || 20
  const resolvedGap = Number.isFinite(gap) ? gap : 6
  return resolvedLine * 2 + resolvedGap + 2
}

/**
 * Breadcrumb list that collapses middle crumbs to an ellipsis when the trail
 * would wrap onto more than two rows.
 */
function CollapsingBreadcrumbList({
  crumbs,
  className,
  maxRows = 2,
}: {
  crumbs: CollapsingBreadcrumbCrumb[]
  className?: string
  maxRows?: number
}) {
  const listRef = React.useRef<HTMLOListElement>(null)
  const crumbsKey = crumbs.map((c) => c.key).join("\0")
  const [{ hiddenMiddleCount, measureGen }, setCollapse] = React.useState({
    hiddenMiddleCount: 0,
    measureGen: 0,
  })

  React.useLayoutEffect(() => {
    setCollapse({ hiddenMiddleCount: 0, measureGen: 0 })
  }, [crumbsKey])

  React.useLayoutEffect(() => {
    const list = listRef.current
    if (!list || maxRows < 2) return undefined

    const maxHide = Math.max(0, crumbs.length - 2)
    const maxHeight = twoLineMaxHeightPx(list)
    if (list.scrollHeight > maxHeight + 1 && hiddenMiddleCount < maxHide) {
      setCollapse((prev) => ({
        ...prev,
        hiddenMiddleCount: prev.hiddenMiddleCount + 1,
      }))
    }

    let lastWidth = list.clientWidth
    const ro = new ResizeObserver(() => {
      const width = list.clientWidth
      if (Math.abs(width - lastWidth) < 1) return
      lastWidth = width
      setCollapse((prev) => ({
        hiddenMiddleCount: 0,
        measureGen: prev.measureGen + 1,
      }))
    })
    ro.observe(list)
    return () => ro.disconnect()
  }, [crumbs.length, crumbsKey, hiddenMiddleCount, measureGen, maxRows])

  const visibleCrumbs = React.useMemo(() => {
    if (hiddenMiddleCount <= 0 || crumbs.length <= 2) {
      return crumbs.map((crumb) => ({ kind: "crumb" as const, crumb }))
    }
    const keepHead = 1
    const hide = Math.min(hiddenMiddleCount, crumbs.length - 2)
    const head = crumbs.slice(0, keepHead)
    const tail = crumbs.slice(keepHead + hide)
    return [
      ...head.map((crumb) => ({ kind: "crumb" as const, crumb })),
      { kind: "ellipsis" as const, key: "ellipsis" },
      ...tail.map((crumb) => ({ kind: "crumb" as const, crumb })),
    ]
  }, [crumbs, hiddenMiddleCount])

  return (
    <BreadcrumbList
      ref={listRef}
      className={cn("max-h-[3.25rem] overflow-hidden", className)}
    >
      {visibleCrumbs.map((entry, index) => {
        const isLast = index === visibleCrumbs.length - 1
        if (entry.kind === "ellipsis") {
          return (
            <React.Fragment key={entry.key}>
              <BreadcrumbItem className="shrink-0">
                <span aria-hidden="true" className="px-0.5 text-muted-foreground">
                  …
                </span>
                <span className="sr-only">More</span>
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </React.Fragment>
          )
        }
        const { crumb } = entry
        return (
          <React.Fragment key={crumb.key}>
            <BreadcrumbItem
              className={cn(
                crumb.truncate && "min-w-0 max-w-[min(100%,18rem)]",
                crumb.itemClassName
              )}
            >
              {crumb.node}
            </BreadcrumbItem>
            {!isLast && (
              <BreadcrumbSeparator className={crumb.separatorClassName} />
            )}
          </React.Fragment>
        )
      })}
    </BreadcrumbList>
  )
}
CollapsingBreadcrumbList.displayName = "CollapsingBreadcrumbList"

const BreadcrumbItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<"li">
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn("inline-flex items-center gap-1.5", className)}
    {...props}
  />
))
BreadcrumbItem.displayName = "BreadcrumbItem"

const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<"a"> & {
    asChild?: boolean
  }
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      ref={ref}
      className={cn("transition-colors hover:text-foreground", className)}
      {...props}
    />
  )
})
BreadcrumbLink.displayName = "BreadcrumbLink"

const BreadcrumbPage = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<"span">
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn("font-normal text-foreground", className)}
    {...props}
  />
))
BreadcrumbPage.displayName = "BreadcrumbPage"

const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn("[&>svg]:w-3.5 [&>svg]:h-3.5", className)}
    {...props}
  >
    {children ?? <ChevronRight />}
  </li>
)
BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

const BreadcrumbEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More</span>
  </span>
)
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis"

export {
  Breadcrumb,
  BreadcrumbList,
  CollapsingBreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
}
